import {
  Badge, Button, Card, DataTable, DateInput, ErrorState, Field, Input, Notice, Permissions, Row, Select, Stack,
  calendarDays, invalidDateIn, useApi, useI18n, usePermission,
  type CalendarEventResponse, type CalendarEventType,
} from '@daghfal/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

const EVENT_TYPES: CalendarEventType[] = ['Closure', 'Holiday', 'Event', 'Trip'];

/** A closure or a holiday is a day the nursery is shut, which the absence rule leaves out. */
const closes = (type: CalendarEventType) => type === 'Closure' || type === 'Holiday';

/**
 * The tenant calendar (FR-MSG-08, FR-MSG-09): added, corrected and removed here (GAPS 3.7).
 *
 * A closure or holiday decides which days count towards absence, so changing or removing one says so before it is
 * saved, and one in a closed term is refused with the API's own words (Q34). Every board and the dashboard are read
 * again after a change.
 */
export function CalendarManager() {
  const api = useApi();
  const { t, formatDate, settings } = useI18n();
  const queryClient = useQueryClient();
  const canManage = usePermission(Permissions.tenant.manageSettings);
  const [editing, setEditing] = useState<CalendarEventResponse | null>(null);
  const [removing, setRemoving] = useState<number | null>(null);

  const events = useQuery({ queryKey: ['calendar'], queryFn: () => api.calendar.list() });

  const remove = useMutation({
    mutationFn: (id: number) => api.calendar.remove(id),
    onSuccess: async () => {
      setRemoving(null);
      await invalidateCalendar(queryClient);
    },
  });

  return (
    <Stack>
      {canManage && (
        <CalendarForm key={editing?.id ?? 'new'} entry={editing} onDone={() => setEditing(null)} />
      )}

      {remove.isError && <ErrorState error={remove.error} />}

      <Card flush>
        <DataTable
          caption={t('calendar.title')}
          rows={events.data}
          rowKey={(event) => event.id}
          isLoading={events.isPending}
          error={events.error}
          onRetry={() => void events.refetch()}
          empty={t('calendar.empty')}
          columns={[
            { key: 'title', header: t('announcements.subject'), render: (e) => e.title },
            { key: 'type', header: t('incidents.type'), render: (e) => t(`calendar.type.${e.eventType}`) },
            {
              key: 'range',
              header: t('invoices.period'),
              render: (e) => {
                const { from, to } = calendarDays(e, settings);
                return `${formatDate(from)} – ${formatDate(to)}`;
              },
            },
            {
              key: 'attendance',
              header: t('calendar.affectsAttendance'),
              render: (e) => e.affectsAttendance ? <Badge tone="warning">✓</Badge> : '—',
            },
            ...(canManage ? [{
              key: 'actions',
              header: '',
              render: (e: CalendarEventResponse) => removing === e.id ? (
                <Stack tight>
                  {e.affectsAttendance && <span className="dg-card__hint">{t('calendar.removeWarning')}</span>}
                  <Row>
                    <Button variant="danger" busy={remove.isPending} onClick={() => remove.mutate(e.id)}>
                      {t('calendar.confirmRemove')}
                    </Button>
                    <Button variant="ghost" onClick={() => setRemoving(null)}>{t('action.cancel')}</Button>
                  </Row>
                </Stack>
              ) : (
                <Row>
                  <Button variant="ghost" aria-label={t('calendar.editOf', { title: e.title })} onClick={() => setEditing(e)}>
                    {t('action.edit')}
                  </Button>
                  <Button variant="ghost" aria-label={t('calendar.removeOf', { title: e.title })}
                    onClick={() => { remove.reset(); setRemoving(e.id); }}>
                    {t('calendar.remove')}
                  </Button>
                </Row>
              ),
            }] : []),
          ]}
        />
      </Card>
    </Stack>
  );
}

/**
 * What a calendar change leaves stale: the calendar, and, since a closure changes who counts as absent, the attendance
 * boards and the dashboard.
 */
function invalidateCalendar(queryClient: ReturnType<typeof useQueryClient>): Promise<void> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ['calendar'] }),
    queryClient.invalidateQueries({ queryKey: ['attendance'] }),
    queryClient.invalidateQueries({ queryKey: ['class-day'] }),
    queryClient.invalidateQueries({ queryKey: ['admin', 'dashboard'] }),
  ]).then(() => undefined);
}

/** Adds an entry as whole days, or corrects one when `entry` is given, keeping the days it had unless they change. */
function CalendarForm({ entry, onDone }: { entry: CalendarEventResponse | null; onDone: () => void }) {
  const api = useApi();
  const { t, settings } = useI18n();
  const queryClient = useQueryClient();

  const [title, setTitle] = useState(entry?.title ?? '');
  const [eventType, setEventType] = useState<CalendarEventType>(entry?.eventType ?? 'Closure');
  // The days the API counts, so a correction saved unchanged keeps them (`calendarDays`).
  const days = entry ? calendarDays(entry, settings) : null;
  const [startAt, setStartAt] = useState(days?.from ?? '');
  const [endAt, setEndAt] = useState(days?.to ?? '');

  const save = useMutation({
    mutationFn: () => {
      // An entry whose days are left as they were keeps its own times, so a timed one (made through the API) is not
      // turned into whole days by correcting its title; and one whose kind is kept keeps its own attendance flag.
      const sameDays = entry !== null && days !== null && startAt === days.from && (endAt || startAt) === days.to;
      const body = {
        title: title.trim(),
        description: entry?.description ?? null,
        eventType,
        startAt: sameDays ? entry.startAt : new Date(`${startAt}T00:00:00Z`).toISOString(),
        endAt: sameDays ? entry.endAt : new Date(`${endAt || startAt}T23:59:59Z`).toISOString(),
        allDay: sameDays ? entry.allDay : true,
        affectsAttendance: entry !== null && eventType === entry.eventType ? entry.affectsAttendance : null,
        affectsBilling: entry?.affectsBilling ?? false,
        audienceClassId: entry?.audienceClassId ?? null,
      };
      return entry ? api.calendar.update(entry.id, body) : api.calendar.create(body);
    },
    onSuccess: async () => {
      setTitle('');
      setStartAt('');
      setEndAt('');
      onDone();
      await invalidateCalendar(queryClient);
    },
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (invalidDateIn(event.currentTarget)) return;
    if (title.trim() && startAt) save.mutate();
  };

  // A correction to a closure, or one that makes or unmakes a closure, changes absence counting for its days.
  const changesAbsence = entry !== null && (closes(entry.eventType) || closes(eventType));

  return (
    <Card title={entry ? t('calendar.editOf', { title: entry.title }) : t('calendar.new')}>
      <form noValidate onSubmit={submit}>
        <Stack>
          {save.isError && <ErrorState error={save.error} />}
          {changesAbsence && <Notice tone="warning">{t('calendar.changeWarning')}</Notice>}

          <Field label={t('announcements.subject')} required>
            {(props) => <Input {...props} value={title} onChange={(event) => setTitle(event.target.value)} required />}
          </Field>

          <Row>
            <Field label={t('incidents.type')}>
              {(props) => (
                <Select {...props} value={eventType} onChange={(event) => setEventType(event.target.value as CalendarEventType)}>
                  {EVENT_TYPES.map((value) => <option key={value} value={value}>{t(`calendar.type.${value}`)}</option>)}
                </Select>
              )}
            </Field>
            <Field label={t('finance.from')} required>
              {(props) => <DateInput {...props} value={startAt} onChange={setStartAt} required />}
            </Field>
            <Field label={t('finance.to')}>
              {(props) => <DateInput {...props} value={endAt} min={startAt || undefined} onChange={setEndAt} />}
            </Field>
          </Row>

          <Row>
            <Button type="submit" busy={save.isPending} busyLabel={t('action.saving')}>{t('action.save')}</Button>
            {entry && <Button variant="ghost" onClick={onDone}>{t('action.cancel')}</Button>}
          </Row>
        </Stack>
      </form>
    </Card>
  );
}
